import { http } from './request'
import { secureLocalStorage } from '@/utils/secureLocalStorage'

/** 与 GlobalAreaTreeResult 一致（treeList 瘦身后仅保留展示/级联所需字段） */
export interface GlobalAreaTreeNode {
  geonameid?: number
  cnName?: string | null
  /** 国家二位码（根节点国家） */
  countryCode?: string | null
  parentGeonameid?: number | null
  regionalLevel?: number | null
  adminDivisionCode?: string | null
  /** 经度（WGS-84） */
  longitude?: number | null
  /** 纬度（WGS-84） */
  latitude?: number | null
  children?: GlobalAreaTreeNode[]
}

export type OverseasGeoCascaderOption = {
  label: string
  value: string
  children?: OverseasGeoCascaderOption[]
}

/**
 * GET /biz/overseas/common/globalcountry/treeList
 * @param countryCodeList 可选，逗号分隔国家二位码，如 HK,TH,JP；不传则返回全部激活国家及下级（与后台 GlobalCountryService#treeList 一致）
 */
type GlobalCountryTreeCacheEntry = {
  at: number
  data: GlobalAreaTreeNode[]
}

export const GLOBAL_COUNTRY_TREE_CACHE_TTL_MS = 24 * 60 * 60 * 1000
/** 过期后仍可先用本地数据打开页面，后台再刷新 */
const GLOBAL_COUNTRY_TREE_MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000
const globalCountryTreeCache = new Map<string, GlobalCountryTreeCacheEntry>()
const globalCountryTreeInflight = new Map<string, Promise<GlobalAreaTreeNode[]>>()
let globalCountryTreeServedAt = 0
const GLOBAL_COUNTRY_TREE_STORAGE_PREFIX = 'yj:globalCountryTree:v3:'

function normalizeCountryCodeListKey(countryCodeList?: string): string {
  const raw = (countryCodeList ?? '').trim()
  return raw ? raw : '__ALL__'
}

function storageKey(cacheKey: string): string {
  return `${GLOBAL_COUNTRY_TREE_STORAGE_PREFIX}${cacheKey}`
}

export function readPersistedGlobalCountryTreeList(
  countryCodeList?: string,
): { at: number; data: GlobalAreaTreeNode[] } | null {
  const entry = readPersistedCache(normalizeCountryCodeListKey(countryCodeList))
  if (!entry?.data?.length) return null
  return entry
}

function readPersistedCache(cacheKey: string): GlobalCountryTreeCacheEntry | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = secureLocalStorage.getItem(storageKey(cacheKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<GlobalCountryTreeCacheEntry>
    if (!parsed || typeof parsed !== 'object') return null
    const at = typeof parsed.at === 'number' ? parsed.at : NaN
    const data = Array.isArray(parsed.data) ? (parsed.data as GlobalAreaTreeNode[]) : null
    if (!Number.isFinite(at) || !data) return null
    return { at, data }
  } catch {
    return null
  }
}

function writePersistedCache(cacheKey: string, entry: GlobalCountryTreeCacheEntry): void {
  try {
    if (typeof window === 'undefined') return
    secureLocalStorage.setItem(storageKey(cacheKey), JSON.stringify(entry))
  } catch {
    // ignore storage quota / privacy mode
  }
}

/**
 * GET /biz/overseas/common/globalcountry/treeList
 * @internal 仅由 `globalCountryCache` 调用；业务请使用 `ensureGlobalCountryTree` 与缓存 refs
 * 内存或本地有数据时直接返回（含过期数据），避免每次打开预警表单都等待慢接口。
 */
export function lastGlobalCountryTreeServedAt(): number {
  return globalCountryTreeServedAt
}

export async function fetchGlobalCountryTreeList(
  countryCodeList?: string,
  forceRefresh = false,
): Promise<GlobalAreaTreeNode[]> {
  const key = normalizeCountryCodeListKey(countryCodeList)
  const now = Date.now()

  if (!forceRefresh) {
    const hit = globalCountryTreeCache.get(key)
    if (hit?.data?.length && now - hit.at <= GLOBAL_COUNTRY_TREE_MAX_STALE_MS) {
      globalCountryTreeServedAt = hit.at
      return hit.data
    }

    const persisted = readPersistedCache(key)
    if (persisted?.data?.length && now - persisted.at <= GLOBAL_COUNTRY_TREE_MAX_STALE_MS) {
      globalCountryTreeCache.set(key, persisted)
      globalCountryTreeServedAt = persisted.at
      return persisted.data
    }
  }

  const inflight = globalCountryTreeInflight.get(key)
  if (inflight) return inflight

  const req = (async () => {
    const params: Record<string, string> = {}
    if (countryCodeList?.trim()) params.countryCodeList = countryCodeList.trim()
    try {
      const raw = await http.get<unknown>('/biz/overseas/common/globalcountry/treeList', {
        params,
        // 该接口数据量可能较大，默认 10s 容易超时；同时关闭全局错误提示，避免重复弹窗
        timeout: 30_000,
        skipErrorMessage: true,
      })
      const data = Array.isArray(raw) ? (raw as GlobalAreaTreeNode[]) : []
      const entry = { at: Date.now(), data }
      if (data.length) {
        globalCountryTreeCache.set(key, entry)
        writePersistedCache(key, entry)
      }
      globalCountryTreeServedAt = entry.at
      return data
    } catch {
      // 网络/后端异常：若已有历史缓存（哪怕过期）则回退，避免弹窗反复报错
      const fallback = globalCountryTreeCache.get(key) ?? readPersistedCache(key)
      if (fallback?.data?.length) {
        globalCountryTreeServedAt = fallback.at
        return fallback.data
      }
      // 首次加载且无历史缓存时：返回空数组，由上层决定是否提示
      return []
    }
  })()

  globalCountryTreeInflight.set(key, req)
  try {
    return await req
  } finally {
    globalCountryTreeInflight.delete(key)
  }
}

export type GlobalCountryDropdownItem = {
  geonameid?: number
  cnName?: string | null
  enName?: string | null
  countryCode?: string | null
  continent?: string | null
  adminDivisionCode?: string | null
}

let countryDropdownCached: { at: number; data: GlobalCountryDropdownItem[] } | null = null
let countryDropdownInflight: Promise<GlobalCountryDropdownItem[]> | null = null
const COUNTRY_DROPDOWN_TTL_MS = 60 * 60 * 1000

/**
 * 国家下拉接口路径在不同部署可能不一致：
 * - 标准（与文档一致）：/biz/overseas/common/globalcountry/countryDropdownList
 * - 当前后端 CommonController 兼容：@GetMapping("run") → /biz/overseas/common/run
 * - 小程序端（部分网关可能复用）：/biz/overseas/common/mini/globalcountry/countryDropdownList
 */
const COUNTRY_DROPDOWN_PATHS = [
  '/biz/overseas/common/globalcountry/countryDropdownList',
  '/biz/overseas/common/run',
  '/biz/overseas/common/mini/globalcountry/countryDropdownList',
] as const

/**
 * 国家下拉列表（后台端）：GET /biz/overseas/common/globalcountry/countryDropdownList
 * 内存缓存，避免各表单重复加载。
 */
export async function fetchCountryDropdownListOnce(): Promise<GlobalCountryDropdownItem[]> {
  const now = Date.now()
  if (countryDropdownCached && now - countryDropdownCached.at <= COUNTRY_DROPDOWN_TTL_MS) {
    return countryDropdownCached.data
  }
  if (!countryDropdownInflight) {
    countryDropdownInflight = (async () => {
      let lastError: unknown
      let data: GlobalCountryDropdownItem[] = []
      for (const path of COUNTRY_DROPDOWN_PATHS) {
        try {
          const raw = await http.get<unknown>(path, {
            timeout: 30_000,
            skipErrorMessage: true,
          })
          data = Array.isArray(raw) ? (raw as GlobalCountryDropdownItem[]) : []
          if (data.length > 0) break
        } catch (e) {
          lastError = e
        }
      }
      // 若全失败则静默返回空数组（上层决定是否提示）
      void lastError
      countryDropdownCached = { at: Date.now(), data }
      return data
    })()
  }
  try {
    return await countryDropdownInflight
  } finally {
    countryDropdownInflight = null
  }
}

/**
 * 下级行政区下拉（主要字段）：GET /biz/overseas/common/globalcountry/areaChildrenDropdownList
 * @param parentGeonameid 父级 geonameid（国家→省、省→市、市→区县）
 */
export async function fetchAreaChildrenDropdownList(
  parentGeonameid: number,
): Promise<GlobalCountryDropdownItem[]> {
  const id = Number(parentGeonameid)
  if (!Number.isFinite(id) || id <= 0) return []
  const raw = await http.get<unknown>(
    '/biz/overseas/common/globalcountry/areaChildrenDropdownList',
    {
      params: { parentGeonameid: id },
      timeout: 30_000,
      skipErrorMessage: true,
    },
  )
  return Array.isArray(raw) ? (raw as GlobalCountryDropdownItem[]) : []
}

export type AdministrativeAreaTreeKeyNode = {
  geonameid?: number
  parentGeonameid?: number
  cnName?: string | null
  regionalLevel?: number | null
  adminDivisionCode?: string | null
  addressCodeLevel?: string | null
  children?: AdministrativeAreaTreeKeyNode[]
}

let administrativeAreaTreeCached: {
  at: number
  key: string
  data: AdministrativeAreaTreeKeyNode[]
} | null = null
let administrativeAreaTreeInflight: Promise<AdministrativeAreaTreeKeyNode[]> | null = null
const ADMIN_AREA_TREE_TTL_MS = 60 * 60 * 1000

/**
 * 行政区划目录树（关键字段）：GET /biz/overseas/common/globalcountry/administrativeAreaTree
 * @param rootAdministrativeCode 可选：不传返回多国根节点；传区划码返回该节点子树
 */
export async function fetchAdministrativeAreaTreeOnce(
  rootAdministrativeCode?: string,
): Promise<AdministrativeAreaTreeKeyNode[]> {
  const key = String(rootAdministrativeCode ?? '').trim()
  const now = Date.now()
  if (
    administrativeAreaTreeCached &&
    administrativeAreaTreeCached.key === key &&
    now - administrativeAreaTreeCached.at <= ADMIN_AREA_TREE_TTL_MS
  ) {
    return administrativeAreaTreeCached.data
  }
  if (!administrativeAreaTreeInflight) {
    administrativeAreaTreeInflight = (async () => {
      const raw = await http.get<unknown>(
        '/biz/overseas/common/globalcountry/administrativeAreaTree',
        {
          params: key ? { rootAdministrativeCode: key } : undefined,
          timeout: 30_000,
          skipErrorMessage: true,
        },
      )
      const data = Array.isArray(raw) ? (raw as AdministrativeAreaTreeKeyNode[]) : []
      administrativeAreaTreeCached = { at: Date.now(), key, data }
      return data
    })()
  }
  try {
    return await administrativeAreaTreeInflight
  } finally {
    administrativeAreaTreeInflight = null
  }
}

export function mapGlobalAreaTreeToCascaderOptions(
  nodes: GlobalAreaTreeNode[] | undefined | null,
): OverseasGeoCascaderOption[] {
  if (!nodes?.length) return []
  return nodes
    .map((n) => {
      const id = n.geonameid != null ? String(n.geonameid) : ''
      const label = String(n.cnName ?? id).trim() || id
      const childOpts = mapGlobalAreaTreeToCascaderOptions(n.children)
      const children = childOpts.length > 0 ? childOpts : undefined
      return { label, value: id, children }
    })
    .filter((x) => x.value)
}

export type GlobalAreaTreeSelectNode = {
  title: string
  value: string
  key: string
  children?: GlobalAreaTreeSelectNode[]
}

/** treeList → TreeSelect 多选数据 */
export function mapGlobalAreaTreeToTreeSelect(
  nodes: GlobalAreaTreeNode[] | undefined | null,
): GlobalAreaTreeSelectNode[] {
  if (!nodes?.length) return []
  const result: GlobalAreaTreeSelectNode[] = []
  for (const n of nodes) {
    const id = n.geonameid != null ? String(n.geonameid) : ''
    if (!id) continue
    const title = String(n.cnName ?? id).trim() || id
    const children = mapGlobalAreaTreeToTreeSelect(n.children)
    const node: GlobalAreaTreeSelectNode = { title, value: id, key: id }
    if (children.length > 0) {
      node.children = children
    }
    result.push(node)
  }
  return result
}

/** 解析逗号分隔 geonameid 列表 */
export function parseGeonameidList(raw: unknown): string[] {
  return String(raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export type TreeSelectLabelValue = { value: string; label: string }

/** TreeSelect 多选值归一化为 geonameid 字符串数组（兼容 number、tree-check-strictly 的 { value, label } 结构） */
export function normalizeTreeSelectGeonameids(raw: unknown): string[] {
  if (raw == null) return []
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as { checked?: unknown; halfChecked?: unknown; value?: unknown }
    if ('checked' in o) {
      return normalizeTreeSelectGeonameids(o.checked)
    }
    if ('value' in o) {
      const v = String(o.value ?? '').trim()
      return v ? [v] : []
    }
  }
  if (Array.isArray(raw)) {
    return raw.flatMap((item) => normalizeTreeSelectGeonameids(item))
  }
  const single = String(raw).trim()
  return single ? [single] : []
}

/** geonameid 数组 → tree-check-strictly 所需的 { value, label } 结构 */
export function geonameidsToTreeSelectLabelValues(
  geonameids: string[],
  nodes: GlobalAreaTreeNode[] | undefined | null,
): TreeSelectLabelValue[] {
  return normalizeTreeSelectGeonameids(geonameids).map((id) => {
    const num = Number(id)
    const label = Number.isFinite(num) && num > 0 ? formatGeonameidAsAreaPath(nodes, num) || id : id
    return { value: id, label }
  })
}

/** 多个 geonameid 格式化为「国家 / 省 / …」展示，顿号分隔 */
export function formatGeonameidListAsAreaPaths(
  nodes: GlobalAreaTreeNode[] | undefined | null,
  geonameids: string[],
): string {
  if (!geonameids.length) return ''
  return geonameids
    .map((id) => {
      const n = Number(id)
      if (!Number.isFinite(n) || n <= 0) return id
      return formatGeonameidAsAreaPath(nodes, n) || id
    })
    .join('、')
}

/**
 * 在全球区划树中按 geonameid 查找从根到该节点的中文/英文名称路径（与级联展示一致）
 */
export function findGeonameidLabelPathInGlobalTree(
  nodes: GlobalAreaTreeNode[] | undefined | null,
  targetGeonameid: number,
  prefixLabels: string[] = [],
): string[] | null {
  if (!nodes?.length) return null
  const target = String(targetGeonameid)
  for (const n of nodes) {
    if (n.geonameid == null) continue
    const id = String(n.geonameid)
    const label = String(n.cnName ?? id).trim() || id
    const next = [...prefixLabels, label]
    if (id === target) return next
    if (n.children?.length) {
      const hit = findGeonameidLabelPathInGlobalTree(n.children, targetGeonameid, next)
      if (hit) return hit
    }
  }
  return null
}

/** 将 geonameid 格式化为「国家 / 省 / 市」展示文案；树中无匹配时返回空串 */
export function formatGeonameidAsAreaPath(
  nodes: GlobalAreaTreeNode[] | undefined | null,
  geonameid: number | null | undefined,
): string {
  if (geonameid == null || Number.isNaN(geonameid) || geonameid <= 0) return ''
  const parts = findGeonameidLabelPathInGlobalTree(nodes, geonameid)
  return parts?.length ? parts.join(' / ') : ''
}

/** 根据末级 geonameid 反查级联 value 路径 */
export function findGeonameidPathInCascaderTree(
  options: OverseasGeoCascaderOption[],
  targetId: string,
  prefix: string[] = [],
): string[] | null {
  const t = targetId.trim()
  if (!t) return null
  for (const o of options) {
    const path = [...prefix, o.value]
    if (o.value === t) return path
    if (o.children?.length) {
      const sub = findGeonameidPathInCascaderTree(o.children, t, path)
      if (sub) return sub
    }
  }
  return null
}

/** 详情展示：优先全路径，否则国家名 + 末级地区名 */
export function formatOverseasRegionDisplay(input: {
  pathLabels?: string[]
  countryName?: string
  lastLevelName?: string
}): string {
  if (input.pathLabels?.length) return input.pathLabels.join(' / ')
  const country = (input.countryName ?? '').trim()
  const last = (input.lastLevelName ?? '').trim()
  if (country && last) {
    if (last === country || last.startsWith(`${country} /`)) return last
    return `${country} / ${last}`
  }
  return country || last
}
